/**
 * TimelineUI.js — Right-side vertical timeline (hidden by default)
 * - Buttons appear on right side, vertically centered
 * - Hidden by default, toggled by Timeline button
 * - Details panel appears to the left of the buttons
 */

const TIMELINE_OPTIONS = {
    position: "right",
    orientation: "vertical",
    paddingY: 100,
    railWidth: 76,
    detailsOffsetX: 20,
};

// -------------------- STYLE INJECTION --------------------
(function injectTimelineStyles() {
    const styleId = "timeline-ui-styles-right-vertical";
    if (document.getElementById(styleId)) return;

    const css = `
:root{
  --timeline-padding-y: ${TIMELINE_OPTIONS.paddingY}px;
  --timeline-rail-w: ${TIMELINE_OPTIONS.railWidth}px;
  --timeline-details-offset-x: ${TIMELINE_OPTIONS.detailsOffsetX}px;

  --shade: rgba(58, 65, 73, 1);
  --shade-60: rgba(58, 65, 73, .6);
  --shade-30: rgba(58, 65, 73, .3);
  --shade-10: rgba(58, 65, 73, .1);
}

/* ======= RAIL (RIGHT, VERTICAL) ======= */
.timeline-year-list-container {
  position: fixed;
  z-index: 999;
  opacity: 1;
  transition: opacity .3s ease-out, transform .3s ease-out;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: auto;
  height: auto;
  max-height: calc(100vh - var(--timeline-padding-y) * 2);
  background: transparent;
  border: none;
  overflow: visible;
  pointer-events: auto;
  display: block;
}
.timeline-year-list-container.visible {
  opacity: 1;
  transform: translateY(-50%);
  pointer-events: auto;
  display: block;
}

/* Scroll column */
.timeline-year-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;
  max-height: calc(100vh - var(--timeline-padding-y) * 2);
}
.timeline-year-list::-webkit-scrollbar { display: none; }

/* Fades (top/bottom) */
.timeline-fade-top,
.timeline-fade-bottom {
  position: absolute;
  left: 0;
  width: 100%;
  height: 40px;
  pointer-events: none;
  z-index: 10;
  transition: opacity .25s ease;
  opacity: 0;
}
.timeline-fade-top {
  top: 0;
  background: linear-gradient(180deg, rgba(0,0,0,.65), rgba(0,0,0,.35), rgba(0,0,0,0));
}
.timeline-fade-bottom {
  bottom: 0;
  background: linear-gradient(0deg, rgba(0,0,0,.65), rgba(0,0,0,.35), rgba(0,0,0,0));
}

/* Year buttons */
.timeline-year-button {
  position: relative;
  z-index: 1;
  height: clamp(22px, 1.5vw, 44px);
  min-width: clamp(50px, 4vw, 80px);
  padding: 0 14px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(9px, 1.2vw, 14px);
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all .25s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  user-select: none;
  white-space: nowrap;
  overflow: visible;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
.timeline-year-button::before {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 60%;
  background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.4));
  border-radius: 2px 0 0 2px;
  transition: transform .25s cubic-bezier(0.4, 0, 0.2, 1);
}
.timeline-year-button:hover {
  z-index: 10;
  background: rgba(0, 0, 0, 0.55);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
  transform: scale(1.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 255, 255, 0.08);
}
.timeline-year-button:hover::before {
  transform: translateY(-50%) scaleY(1);
}
.timeline-year-button.selected {
  z-index: 5;
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.05);
}
.timeline-year-button.selected::before {
  transform: translateY(-50%) scaleY(1);
  background: linear-gradient(180deg, #fff, rgba(255,255,255,0.6));
}
.timeline-year-button:active { 
  transform: scale(.97);
  transition-duration: .1s;
}

/* ======= EVENT DETAILS (LEFT OF THE RAIL) ======= */
.timeline-ui-container {
  position: fixed;
  right: calc(20px + var(--timeline-rail-w) + var(--timeline-details-offset-x));
  top: 50%;
  transform: translateY(-50%) translateX(10px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 900;
  opacity: 0;
  transition: opacity .3s ease-out, transform .3s ease-out;
  pointer-events: none;
}
.timeline-ui-container.visible {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
  pointer-events: auto;
}

/* Event card */
.timeline-event-details {
  position: relative;
  width: 380px;
  max-width: min(90vw, 420px);
  min-height: 200px;
  max-height: 50vh;
  background: rgba(0,0,0,.4);
  border: 1px solid var(--shade-30);
  border-radius: 14px;
  backdrop-filter: blur(15px);
  padding: 18px;
  opacity: 0;
  transform: translateX(6px);
  transition: opacity .3s ease-out, transform .3s ease-out, box-shadow .2s ease;
  pointer-events: none;
  overflow-y: auto;
}
.timeline-event-details.visible {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}
.timeline-ui-container:hover .timeline-event-details.visible {
  border-color: var(--shade);
  box-shadow: 0 0 24px rgba(58,65,73,.55);
}

/* Content */
.timeline-event-header {
  margin-bottom: 12px;
  border-bottom: 1px solid var(--shade-30);
  padding-bottom: 8px;
}
.timeline-event-header-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.timeline-event-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.timeline-event-year {
  font-size: clamp(11px, 1.5vw, 22px);
  font-weight: clamp(400, 1.5vw, 800);
  color: var(--shade);
  margin: 0 0 4px 0;
  text-shadow: 0 0 10px rgba(58,65,73,.45);
}
.timeline-event-title {
  font-size: clamp(12px, 1.5vw, 18px);
  font-weight: clamp(400, 1.5vw, 800);
  color: #fff;
  margin: 0;
  text-shadow: 0 0 5px rgba(255,255,255,.3);
}
.timeline-event-description {
  color: rgba(255,255,255,.92);
  font-size: 15px;
  line-height: 1.6;
}

/* Learn More Button */
.timeline-learn-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  white-space: nowrap;
  flex-shrink: 0;
}
.timeline-learn-more-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
}
.timeline-learn-more-btn:active {
  transform: translateY(0);
}
.timeline-learn-more-btn i {
  font-size: 0.8rem;
}
.timeline-learn-more-btn.loading {
  pointer-events: none;
  opacity: 0.7;
}
.timeline-learn-more-btn.loading i {
  animation: timeline-spin 1s linear infinite;
}
@keyframes timeline-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Close button - Modern minimal design */
.timeline-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0.5;
  transition: opacity .2s ease, transform .2s ease;
}
.timeline-close-btn::before,
.timeline-close-btn::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 1.5px;
  background: #fff;
  border-radius: 1px;
  transition: background .2s ease, transform .2s ease;
}
.timeline-close-btn::before {
  transform: rotate(45deg);
}
.timeline-close-btn::after {
  transform: rotate(-45deg);
}
.timeline-close-btn:hover {
  opacity: 1;
}
.timeline-close-btn:hover::before {
  transform: rotate(45deg) scaleX(1.1);
}
.timeline-close-btn:hover::after {
  transform: rotate(-45deg) scaleX(1.1);
}
.timeline-close-btn:active { 
  transform: scale(.9);
  transition-duration: .1s;
}

/* Scrollbar inside details */
.timeline-event-details::-webkit-scrollbar { width: 4px; }
.timeline-event-details::-webkit-scrollbar-track { background: rgba(0,0,0,.1); border-radius: 2px; }
.timeline-event-details::-webkit-scrollbar-thumb { background: rgba(58,65,73,.5); border-radius: 2px; }
.timeline-event-details::-webkit-scrollbar-thumb:hover { background: rgba(58,65,73,.8); }

/* No data */
.no-timeline-data {
  color: rgba(255,255,255,.75);
  font-size: 12px;
  text-align: center;
  padding: 16px;
  font-style: italic;
}

/* Responsive tweaks */
@media (max-width: 1024px) {
  .timeline-event-details { width: 320px; }
  .timeline-year-list-container { right: 15px; }
}
@media (max-width: 768px) {
  .timeline-year-list { gap: 8px; }
  .timeline-year-button { height: 36px; min-width: 60px; font-size: 12px; }
  .timeline-event-details { width: 280px; }
  .timeline-year-list-container { right: 10px; }
}
@media (max-width: 480px) {
  .timeline-year-button { height: 32px; min-width: 50px; font-size: 11px; }
  .timeline-event-details { width: 240px; max-height: 46vh; }
  .timeline-year-list-container { right: 8px; }
}
  `.trim();

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = css;
    document.head.appendChild(style);
})();

// -------------------- MAIN CLASS --------------------
import { getTimelineData } from "./TimelineData.js";

export class TimelineUI {
    constructor() {
        this.currentCharacterId = null;
        this.currentTimelineData = null;
        this.selectedEventIndex = null;
        this.isEventDetailsVisible = false;
        this.isTimelineVisible = true;
        this.buttonsBound = false;

        this.createTimelineUI();
        this.initializeEventListeners();
    }

    createTimelineUI() {
        // Details container
        this.timelineContainer = document.createElement("div");
        this.timelineContainer.id = "timeline-ui-container";
        this.timelineContainer.className = "timeline-ui-container";

        // Event details panel
        this.eventDetailsPanel = document.createElement("div");
        this.eventDetailsPanel.className = "timeline-event-details";
        this.timelineContainer.appendChild(this.eventDetailsPanel);

        // Rail container (right side, vertical)
        this.yearListContainer = document.createElement("div");
        this.yearListContainer.id = "timeline-year-list-container";
        this.yearListContainer.className = "timeline-year-list-container";

        // Scrollable column
        this.yearList = document.createElement("div");
        this.yearList.className = "timeline-year-list";

        // Fades (top/bottom for vertical scroll)
        this.topFade = document.createElement("div");
        this.topFade.className = "timeline-fade-top";
        this.bottomFade = document.createElement("div");
        this.bottomFade.className = "timeline-fade-bottom";

        // Compose
        this.yearListContainer.appendChild(this.topFade);
        this.yearListContainer.appendChild(this.yearList);
        this.yearListContainer.appendChild(this.bottomFade);

        document.body.appendChild(this.timelineContainer);
        document.body.appendChild(this.yearListContainer);

        // Timeline is always visible
        this.yearListContainer.style.display = "block";
        this.yearListContainer.classList.add("visible");
    }

    initializeEventListeners() {
        document.addEventListener("modelLoadingComplete", () =>
            this.handleModelChange(),
        );
        document.addEventListener("character-changed", (event) => {
            if (event.detail && event.detail.modelId)
                this.handleModelChange(event.detail.modelId);
        });

        this.yearList.addEventListener("scroll", () =>
            this.updateFadeEffects(),
        );
        window.addEventListener("resize", () => this.updateFadeEffects());

        document.addEventListener("click", (event) => {
            if (
                this.isEventDetailsVisible &&
                !this.timelineContainer.contains(event.target) &&
                !this.yearListContainer.contains(event.target)
            ) {
                this.closeEventDetails();
            }
        });
    }

    bindTimelineButton() {
        // Guard against duplicate bindings
        if (this.buttonsBound) return;
        this.buttonsBound = true;

        // Desktop button
        const timelineBtn = document.getElementById("timeline-action-button");
        if (timelineBtn) {
            timelineBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                this.toggleTimeline();
            });
        }

        // Mobile wheel button
        const wheelItems = document.querySelectorAll('.action-wheel-item[data-action="timeline"]');
        wheelItems.forEach((item) => {
            const btn = item.querySelector("button");
            if (btn) {
                btn.addEventListener("click", (e) => {
                    e.stopPropagation();
                    this.toggleTimeline();
                });
            }
        });
    }

    toggleTimeline() {
        if (this.isTimelineVisible) {
            this.hideTimeline();
        } else {
            this.showTimeline();
        }
    }

    showTimeline() {
        // Load data if not already loaded
        const modelId = this.getCurrentModelId();
        if (modelId && modelId !== this.currentCharacterId) {
            this.handleModelChange(modelId);
        }

        if (!this.currentTimelineData) {
            this.loadTimelineData(this.getCurrentModelId());
        }

        this.yearListContainer.style.display = "block";
        this.isTimelineVisible = true;
        requestAnimationFrame(() => {
            this.yearListContainer.classList.add("visible");
        });
    }

    hideTimeline() {
        // Timeline is always visible, only close event details
        this.closeEventDetails();
    }

    handleModelChange(modelId = null) {
        if (!modelId) modelId = this.getCurrentModelId();
        if (modelId && modelId !== this.currentCharacterId) {
            this.currentCharacterId = modelId;
            this.loadTimelineData(modelId);
        }
    }

    getCurrentModelId() {
        if (window.modelViewer && window.modelViewer.currentModelKey) {
            return window.modelViewer.currentModelKey;
        }
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("character") || null;
    }

    loadTimelineData(characterId) {
        this.currentTimelineData = getTimelineData(characterId);
        if (this.currentTimelineData) {
            this.renderYearButtons();
        } else {
            this.showNoDataMessage();
        }
    }

    renderYearButtons() {
        if (!this.currentTimelineData) return;
        this.yearList.innerHTML = "";

        const events = this.currentTimelineData.events || [];

        const makeBtn = (event, index) => {
            const btn = document.createElement("button");
            btn.className = "timeline-year-button";
            btn.textContent = event.year;
            btn.dataset.eventIndex = index;
            btn.addEventListener("click", () => this.selectEvent(index));
            return btn;
        };

        // Vertical layout - all buttons stacked
        events.forEach((e, i) => this.yearList.appendChild(makeBtn(e, i)));

        this.updateFadeEffects();
    }

    selectEvent(eventIndex) {
        if (
            !this.currentTimelineData ||
            eventIndex >= this.currentTimelineData.events.length
        )
            return;

        this.selectedEventIndex = eventIndex;
        const evt = this.currentTimelineData.events[eventIndex];

        this.yearList
            .querySelectorAll(".timeline-year-button")
            .forEach((button, idx) => {
                button.classList.toggle("selected", idx === eventIndex);
            });

        this.displayEventDetails(evt);
        this.showEventDetails();
    }

    displayEventDetails(event) {
        this.eventDetailsPanel.innerHTML = `
      <div class="timeline-event-header">
        <div class="timeline-event-header-row">
          <h3 class="timeline-event-year">${event.year ?? ""}</h3>
          <div class="timeline-event-title-row">
            <h4 class="timeline-event-title">${event.title ?? ""}</h4>
            <button class="timeline-learn-more-btn" title="Let the AI tell you more about this event">
              <i class="fas fa-magic"></i>
              <span>Learn More</span>
            </button>
          </div>
        </div>
      </div>
      <div class="timeline-event-description">
        <p>${event.description ?? ""}</p>
      </div>
      <button class="timeline-close-btn" aria-label="Close" onclick="window.timelineUI.closeEventDetails()"></button>
    `;

        // Bind the Learn More button click handler
        const learnMoreBtn = this.eventDetailsPanel.querySelector('.timeline-learn-more-btn');
        if (learnMoreBtn) {
            learnMoreBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleLearnMore(event);
            });
        }
    }

    showEventDetails() {
        this.timelineContainer.style.display = "flex";
        this.isEventDetailsVisible = true;
        requestAnimationFrame(() => {
            this.timelineContainer.classList.add("visible");
            this.eventDetailsPanel.classList.add("visible");
        });
    }

    closeEventDetails() {
        this.timelineContainer.classList.remove("visible");
        this.eventDetailsPanel.classList.remove("visible");

        setTimeout(() => {
            this.timelineContainer.style.display = "none";
            this.isEventDetailsVisible = false;
        }, 250);

        this.selectedEventIndex = null;
        this.yearList
            .querySelectorAll(".timeline-year-button")
            .forEach((b) => b.classList.remove("selected"));
    }

    updateFadeEffects() {
        const el = this.yearList;
        const scrollTop = el.scrollTop;
        const max = el.scrollHeight - el.clientHeight;
        if (this.topFade) this.topFade.style.opacity = scrollTop > 0 ? "1" : "0";
        if (this.bottomFade) this.bottomFade.style.opacity = scrollTop < max - 5 ? "1" : "0";
    }

    showNoDataMessage() {
        this.yearList.innerHTML =
            '<div class="no-timeline-data">No timeline data available for this character</div>';
        this.closeEventDetails();
    }

    getCurrentTimelineData() {
        return this.currentTimelineData;
    }
    getIsEventDetailsVisible() {
        return this.isEventDetailsVisible;
    }
    getIsTimelineVisible() {
        return this.isTimelineVisible;
    }

    /**
     * Handle "Learn More" button click for a timeline event
     * Sends the event to the chat system silently and displays AI-enhanced response
     * @param {object} event - The timeline event object
     */
    async handleLearnMore(event) {
        console.log(`[TimelineUI] handleLearnMore called for event: "${event.title}"`);
        
        if (!event) {
            console.warn("[TimelineUI] No event data available");
            return;
        }

        // Get character name from current model
        let characterName = "the character";
        if (window.modelViewer && window.modelViewer.currentModelKey) {
            characterName = window.modelViewer.currentModelKey;
        }

        // Find the button and set loading state
        const button = this.eventDetailsPanel?.querySelector('.timeline-learn-more-btn');
        if (button) {
            button.classList.add("loading");
            const icon = button.querySelector("i");
            if (icon) {
                icon.className = "fas fa-spinner";
            }
        }

        try {
            console.log(`[TimelineUI] Sending event "${event.title}" for ${characterName} to chat for AI enhancement`);

            // Create a prompt for this specific timeline event
            const eventPrompt = `I'd like you to tell me more about this historical event from ${characterName}'s life. Here's the event:\n\n**${event.year}: ${event.title}**\n\n${event.description}\n\nPlease tell me more about this event in your own words, making it more vivid and engaging while keeping the historical facts accurate. Focus on bringing the narrative to life and providing additional context.`;

            // Send to chat system silently (without showing the user's message)
            await this.sendEventSilentlyToChat(eventPrompt, characterName);

        } catch (error) {
            console.error("[TimelineUI] Error handling Learn More:", error);
        } finally {
            // Reset loading state
            if (button) {
                button.classList.remove("loading");
                const icon = button.querySelector("i");
                if (icon) {
                    icon.className = "fas fa-magic";
                }
            }
        }
    }

    /**
     * Send timeline event to chat system silently without displaying user message
     * Uses the proper sendMessageSilently method which handles auth, quota, etc.
     * @param {string} eventPrompt - The prompt containing the event details
     * @param {string} characterName - The name of the character
     */
    async sendEventSilentlyToChat(eventPrompt, characterName) {
        console.log(`[TimelineUI] sendEventSilentlyToChat called for ${characterName}`);
        
        // Get reference to the chat controller if it exists
        const chatController = window.chatController;
        
        if (!chatController) {
            console.error("[TimelineUI] Chat controller not found");
            return;
        }
        console.log(`[TimelineUI] Chat controller found, isEnabled: ${chatController.isEnabled}`);

        // Check if chat is enabled
        if (!chatController.isEnabled) {
            console.warn("[TimelineUI] Chat is not enabled yet");
            return;
        }

        // Close the timeline event details first
        console.log("[TimelineUI] Closing timeline event details");
        this.closeEventDetails();

        try {
            // Use the proper sendMessageSilently method
            // This handles auth checks, quota, interruptions, and doesn't show user message
            console.log("[TimelineUI] Calling sendMessageSilently...");
            const response = await chatController.sendMessageSilently(eventPrompt, "timeline_enhancement");
            console.log("[TimelineUI] sendMessageSilently response:", response);

            if (response && response.status === "success") {
                console.log(`[TimelineUI] Timeline event enhancement completed for ${characterName}`);
            } else if (response && response.status === "error") {
                console.warn(`[TimelineUI] Timeline event enhancement failed: ${response.error}`);
            }

        } catch (error) {
            console.error("[TimelineUI] Failed to send event to chat:", error);
        }
    }
}

// Global instance
window.timelineUI = new TimelineUI();
